import type { IdeaCardInputData } from '@/lib/idea-card-types';

export interface IdeaCardParserLineRange {
  start: number;
  end: number;
}

export interface IdeaCardParserContext {
  text: string;
  lines: string[];
  isPartOfTable: boolean[];
}

export interface IdeaCardParserPhaseResult {
  cards: IdeaCardInputData[];
  usedRanges: IdeaCardParserLineRange[];
  minimumCards?: number;
}

export type IdeaCardParserPhase = (
  context: IdeaCardParserContext
) => IdeaCardParserPhaseResult | null;

export const dedupeParsedCards = <T extends { title: string }>(cards: T[], limit = 24): T[] => {
  const seen = new Set<string>();

  return cards.filter(card => {
    const key = card.title.toLowerCase();

    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, limit);
};

export const shouldExtractParsedCards = (cards: ArrayLike<unknown>, minimumCards = 2): boolean =>
  cards.length >= minimumCards;

export const maskLinesByRanges = (
  lines: string[],
  ranges: IdeaCardParserLineRange[]
): boolean[] => {
  const mask: boolean[] = lines.map(() => true);

  for (const range of ranges) {
    const start = Math.max(0, range.start);
    const end = Math.min(lines.length - 1, range.end);

    for (let index = start; index <= end; index++) {
      mask[index] = false;
    }
  }

  return mask;
};

export const computeRemainderFromRanges = (
  lines: string[],
  ranges: IdeaCardParserLineRange[],
  options?: { insertPlaceholders?: boolean }
): string => {
  if (ranges.length === 0) return lines.join('\n').trim();

  if (options?.insertPlaceholders) {
    // Insert placeholders at card positions
    const result: string[] = [];
    const sortedRanges = [...ranges].sort((a, b) => a.start - b.start);
    let currentLine = 0;

    sortedRanges.forEach((range, idx) => {
      // Add lines before this range
      for (let i = currentLine; i < range.start; i++) {
        result.push(lines[i]);
      }
      // Insert placeholder
      result.push(`[HORIZONTAL_CARD:${idx}]`);
      currentLine = range.end + 1;
    });

    // Add remaining lines
    for (let i = currentLine; i < lines.length; i++) {
      result.push(lines[i]);
    }

    return result.join('\n').trim();
  }

  const mask = maskLinesByRanges(lines, ranges);
  return lines.filter((_, index) => mask[index]).join('\n').trim();
};
